import Component from '@glimmer/component';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import type IntlService from 'ember-intl/services/intl';
import Button from 'tio-ui/components/button';
import { PhPlus, PhX } from 'ember-phosphor-icons';
import { getTypeConfig } from './field-list';
import FilterRow from './filter-row';
import {
  isDateType,
  isNumericType,
  isBooleanType,
  hasEnumValues,
  getEnumItems,
  getEnumFilterItems,
  hasDistinctValues,
  getDistinctItems,
  isTextType,
  getFilterValue,
  getFilterRangeStart,
  getFilterRangeEnd,
} from '../../utils/filter-type-helpers';
import {
  getOperatorsForCategory,
  getDefaultOperator,
  getOperatorInfo,
  operatorDescriptionKey,
  operatorLabelKey,
  parseFilterKey,
  findGroupForField,
  type FilterOperator,
  type FilterLayout,
} from '../../utils/filter-operators';
import { getRelativeDateItems } from '../../utils/relative-date-resolver';
import {
  buildDateWindowForField,
  dateRangeForField,
  dateWindowWarningMessage,
  laterIsoDate,
} from '../../utils/date-filter-window';
import {
  getColumnDivisor,
  rawToDisplayNumber,
  displayToRawNumber,
} from '../../utils/filter-number-format';
import type { ColumnFormatMap } from '../../utils/column-format-options';
import type { FieldOption } from '../../utils/opensearch-field-mapper';

export interface ActiveFiltersZoneSignature {
  Element: HTMLDivElement;
  Args: {
    title?: string;
    subtitle?: string;
    filters: FieldOption[];
    filterValues: Record<string, unknown>;
    /** Omit to hide the per-filter remove ✕ (e.g. viewers adjusting a fixed set). */
    onRemove?: (field: FieldOption) => void;
    onUpdateValue: (key: string, value: unknown) => void;
    /** Bulk update multiple filter keys at once (used for operator changes) */
    onUpdateValues?: (updates: Record<string, unknown>) => void;
    /** Explicitly stored operators per field key */
    filterOperators?: Record<string, FilterOperator>;
    /** Notify parent when operator changes for a field */
    onOperatorChange?: (fieldKey: string, operator: FilterOperator) => void;
    distinctValuesMap?: Record<string, string[]>;
    /**
     * Column format map — used to convert numeric filter inputs to/from the
     * display units shown in the table (e.g. cents↔dollars via divisor).
     */
    columnFormatMap?: ColumnFormatMap;
    /**
     * Optional layout for filter grouping (build filters). When omitted,
     * filters render as a flat list (legacy / user-filters behavior).
     */
    layout?: FilterLayout;
    /** Append a new empty OR group (only meaningful when layout is provided) */
    onAddGroup?: () => void;
    /** Dissolve a group, promoting children back to top level */
    onDissolveGroup?: (groupId: string) => void;
    /** Move a top-level filter into an existing group */
    onMoveToGroup?: (fieldKey: string, groupId: string) => void;
    /** Move a grouped filter back out to top level */
    onMoveOutOfGroup?: (fieldKey: string) => void;
    /**
     * The report/dashboard's build filters, so a viewer's date filter can be
     * reconciled against the hard window they impose (clamp the calendar
     * pickers, warn when a relative preset reaches outside it). Only passed in
     * view mode; absent in the builders, where nothing clamps.
     */
    buildFilterValues?: Record<string, unknown>;
    buildFilterLayout?: FilterLayout;
  };
}

class ActiveFiltersZone extends Component<ActiveFiltersZoneSignature> {
  @service declare intl: IntlService;

  get filterCount() {
    return this.args.filters.length;
  }

  get hasFilters() {
    // In layout mode, render the slot list even if it has only empty groups
    if (this.args.layout && this.args.layout.length > 0) return true;
    return this.filterCount > 0;
  }

  getCategory = (field: FieldOption) => {
    if (isDateType(field)) return 'date' as const;
    if (isNumericType(field)) return 'number' as const;
    if (isBooleanType(field)) return 'boolean' as const;
    if (hasEnumValues(field)) return 'select' as const;
    return 'text' as const;
  };

  /**
   * Detect the current operator for a field.
   * Uses explicitly stored operator if available, otherwise infers from filter value keys.
   */
  detectOperator = (field: FieldOption): FilterOperator => {
    // Prefer explicitly stored operator
    const stored = this.args.filterOperators?.[field.key];
    if (stored) return stored;

    // Fallback: infer from filter value keys
    const filters = this.args.filterValues;

    // Check for between (paired gte+lte) first
    const hasGte =
      filters[`${field.key}__gte`] !== undefined && filters[`${field.key}__gte`] !== null;
    const hasLte =
      filters[`${field.key}__lte`] !== undefined && filters[`${field.key}__lte`] !== null;
    if (hasGte && hasLte) return 'between';

    const suffixedKeys = Object.keys(filters).filter(
      (k) => k.startsWith(`${field.key}__`) && filters[k] !== undefined && filters[k] !== null
    );

    for (const key of suffixedKeys) {
      const { operator } = parseFilterKey(key);
      if (operator !== 'eq') return operator;
    }

    if (filters[field.key] !== undefined && filters[field.key] !== null) return 'eq';

    const category = this.getCategory(field);
    return getDefaultOperator(category);
  };

  getOperatorItems = (field: FieldOption) => {
    const category = this.getCategory(field);
    return getOperatorsForCategory(category).map((op) => {
      const descriptionKey = operatorDescriptionKey(op.key);
      return {
        key: op.key,
        label: this.intl.t(operatorLabelKey(op.key)),
        description: descriptionKey ? this.intl.t(descriptionKey) : undefined,
      };
    });
  };

  /**
   * Handle operator change — clear old filter keys and set new operator state.
   */
  handleOperatorChange = (fieldKey: string, newOp: string) => {
    if (!newOp) return;
    const op = newOp as FilterOperator;

    const clearUpdates: Record<string, unknown> = {};
    for (const key of Object.keys(this.args.filterValues)) {
      if (key === fieldKey || key.startsWith(`${fieldKey}__`)) {
        clearUpdates[key] = undefined;
      }
    }

    // For operators that don't require a value, set immediately
    const info = getOperatorInfo(op);
    if (!info.requiresValue && !info.requiresRange) {
      if (op === 'is_empty') {
        clearUpdates[`${fieldKey}__is_empty`] = true;
      } else if (op === 'is_not_empty') {
        clearUpdates[`${fieldKey}__is_not_empty`] = true;
      }
    } else if (op === 'in' || op === 'not_in') {
      // Multi-select operators initialize with empty array
      clearUpdates[`${fieldKey}__${op}`] = [];
    } else if (op === 'eq') {
      // For eq, set the base key to empty string as placeholder
      clearUpdates[fieldKey] = '';
    } else if (info.requiresRange) {
      // For between, set both range keys as placeholders
      clearUpdates[`${fieldKey}__gte`] = '';
      clearUpdates[`${fieldKey}__lte`] = '';
    } else {
      // For other operators, set suffixed key as placeholder
      clearUpdates[`${fieldKey}__${op}`] = '';
    }

    // Use bulk update if available, otherwise clear one by one
    if (this.args.onUpdateValues) {
      this.args.onUpdateValues(clearUpdates);
    } else {
      for (const [key, value] of Object.entries(clearUpdates)) {
        this.args.onUpdateValue(key, value);
      }
    }

    this.args.onOperatorChange?.(fieldKey, op);
  };

  handleValueForOperator = (fieldKey: string, operator: FilterOperator, value: string) => {
    if (operator === 'eq') {
      this.args.onUpdateValue(fieldKey, value || undefined);
    } else {
      this.args.onUpdateValue(`${fieldKey}__${operator}`, value || undefined);
    }
  };

  /** Divisor for a field's number-formatted column, or undefined. */
  private divisorFor(fieldKey: string): number | undefined {
    return getColumnDivisor(this.args.columnFormatMap, fieldKey);
  }

  /** Convert a stored raw number to its display string (applies divisor). */
  private displayNumber(fieldKey: string, raw: unknown): string {
    if (raw === undefined || raw === null || raw === '') return '';
    const num = Number(raw);
    if (isNaN(num)) return String(raw);
    return String(rawToDisplayNumber(num, this.divisorFor(fieldKey)));
  }

  /** Convert a user-entered display string to a stored raw number. */
  private storeNumber(fieldKey: string, value: string): number | undefined {
    if (!value) return undefined;
    const num = Number(value);
    if (isNaN(num)) return undefined;
    return displayToRawNumber(num, this.divisorFor(fieldKey));
  }

  handleNumberForOperator = (fieldKey: string, operator: FilterOperator, value: string) => {
    const numValue = this.storeNumber(fieldKey, value);
    if (operator === 'eq') {
      this.args.onUpdateValue(fieldKey, numValue);
    } else {
      this.args.onUpdateValue(`${fieldKey}__${operator}`, numValue);
    }
  };

  getFilterValue = (key: string) => getFilterValue(key, this.args.filterValues);

  getFilterRangeStart = (key: string) => {
    const raw = getFilterRangeStart(key, this.args.filterValues);
    return this.divisorFor(key) ? this.displayNumber(key, raw) : raw;
  };

  getFilterRangeEnd = (key: string) => {
    const raw = getFilterRangeEnd(key, this.args.filterValues);
    return this.divisorFor(key) ? this.displayNumber(key, raw) : raw;
  };

  // ── Build-filter date window (clamp viewer pickers + warn on overflow) ──
  // Null unless build filters were supplied (view mode) and pin this field;
  // in the builders no window is passed, so these are all no-ops.

  dateBuildWindow = (field: FieldOption) =>
    this.args.buildFilterValues
      ? buildDateWindowForField(
          this.args.buildFilterValues,
          this.args.buildFilterLayout ?? [],
          field.key
        )
      : null;

  dateWindowMin = (field: FieldOption) => this.dateBuildWindow(field)?.gte ?? '';

  dateWindowMax = (field: FieldOption) => this.dateBuildWindow(field)?.lte ?? '';

  /**
   * `between` end-picker floor: the LATER of the chosen start and the window
   * minimum, so a saved start predating the window can't drop the end picker
   * below the hard window bound.
   */
  dateRangeEndMin = (field: FieldOption) =>
    laterIsoDate(getFilterRangeStart(field.key, this.args.filterValues), this.dateWindowMin(field));

  /** Enum dropdown items — fetched distinct set (incl. empty) or the full enum. */
  getEnumFilterItems = (field: FieldOption) =>
    getEnumFilterItems(field, this.args.distinctValuesMap, this.#translate);

  /** Warning when the viewer's date selection reaches outside the build window. */
  dateWindowWarning = (field: FieldOption) =>
    dateWindowWarningMessage(
      dateRangeForField(this.args.filterValues, field.key),
      this.dateBuildWindow(field)
    );

  getValueForOperator = (field: FieldOption): string => {
    const op = this.detectOperator(field);
    const raw =
      op === 'eq'
        ? this.args.filterValues[field.key]
        : this.args.filterValues[`${field.key}__${op}`];
    if (Array.isArray(raw)) return raw.join(', ');
    // Numeric fields with a divisor format display in user-facing units
    if (this.divisorFor(field.key) && raw !== undefined && raw !== null && raw !== '') {
      return this.displayNumber(field.key, raw);
    }
    // Use a null check (not falsy) so a stored 0 renders as "0", not ""
    return raw != null ? String(raw) : '';
  };

  handleSelectChange = (fieldKey: string, value: string) => {
    this.args.onUpdateValue(fieldKey, value);
  };

  /**
   * The from/to range inputs only render for the `between` operator, so any
   * edit to them implies between. Commit it explicitly — otherwise, with only
   * one bound filled, detectOperator's fallback infers gte/lte and the
   * operator visibly flips away from "between".
   */
  private commitBetween(fieldKey: string) {
    if (this.args.filterOperators?.[fieldKey] !== 'between') {
      this.args.onOperatorChange?.(fieldKey, 'between');
    }
  }

  handleDateStartChange = (fieldKey: string, _dates: Date[], dateStr: string) => {
    this.commitBetween(fieldKey);
    this.args.onUpdateValue(`${fieldKey}__gte`, dateStr || undefined);
  };

  handleDateEndChange = (fieldKey: string, _dates: Date[], dateStr: string) => {
    this.commitBetween(fieldKey);
    this.args.onUpdateValue(`${fieldKey}__lte`, dateStr || undefined);
  };

  handleDateValueForOperator = (
    fieldKey: string,
    operator: FilterOperator,
    _dates: Date[],
    dateStr: string
  ) => {
    this.handleValueForOperator(fieldKey, operator, dateStr);
  };

  handleNumberMinChange = (fieldKey: string, value: string) => {
    this.commitBetween(fieldKey);
    this.args.onUpdateValue(`${fieldKey}__gte`, this.storeNumber(fieldKey, value));
  };

  handleNumberMaxChange = (fieldKey: string, value: string) => {
    this.commitBetween(fieldKey);
    this.args.onUpdateValue(`${fieldKey}__lte`, this.storeNumber(fieldKey, value));
  };

  handleBooleanChange = (fieldKey: string, value: string) => {
    this.args.onUpdateValue(
      fieldKey,
      value === 'true' ? true : value === 'false' ? false : undefined
    );
  };

  isDateType = isDateType;
  isNumericType = isNumericType;
  isBooleanType = isBooleanType;
  hasEnumValues = hasEnumValues;
  isTextType = isTextType;

  getEnumItems = (field: FieldOption) => getEnumItems(field, this.#translate);

  hasDistinctValues = (field: FieldOption) => hasDistinctValues(field, this.args.distinctValuesMap);

  getDistinctItems = (field: FieldOption) =>
    getDistinctItems(field, this.args.distinctValuesMap, this.#translate);

  #translate = (key: string) => this.intl.t(key);

  /** Template helper: true for is_empty/is_not_empty operators */
  isNoValueOperator = (op: FilterOperator) => op === 'is_empty' || op === 'is_not_empty';

  /** Template helper: true for is_empty */
  isEmptyOp = (op: FilterOperator) => op === 'is_empty';

  /** Template helper: true for between */
  isBetweenOp = (op: FilterOperator) => op === 'between';

  /** Template helper: true for in/not_in multi-select operators */
  isMultiSelectOp = (op: FilterOperator) => op === 'in' || op === 'not_in';

  /** Template helper: true for relative_date operator */
  isRelativeDateOp = (op: FilterOperator) => op === 'relative_date';

  /** Relative date preset items for the dropdown */
  relativeDateItems = getRelativeDateItems();

  handleRelativeDateChange = (fieldKey: string, value: string) => {
    this.args.onUpdateValue(`${fieldKey}__relative_date`, value || undefined);
  };

  getMultiSelectValues = (field: FieldOption): string[] => {
    const op = this.detectOperator(field);
    const key = op === 'eq' ? field.key : `${field.key}__${op}`;
    const raw = this.args.filterValues[key];
    if (Array.isArray(raw)) return raw as string[];
    if (typeof raw === 'string' && raw) return raw.split(',');
    return [];
  };

  handleMultiSelectChange = (fieldKey: string, operator: FilterOperator, keys: string[]) => {
    const filterKey = operator === 'eq' ? fieldKey : `${fieldKey}__${operator}`;
    this.args.onUpdateValue(filterKey, keys.length > 0 ? keys : undefined);
  };

  /** True when layout mode is active (grouping supported) */
  get isLayoutMode(): boolean {
    return Array.isArray(this.args.layout);
  }

  /** Lookup helper: field key → FieldOption */
  get fieldByKey(): Record<string, FieldOption> {
    const out: Record<string, FieldOption> = {};
    for (const f of this.args.filters) out[f.key] = f;
    return out;
  }

  /**
   * Synthesize the display structure. If a layout is provided, walk it
   * (single filters at top level + OR groups). Otherwise produce a flat
   * list mirroring @filters (legacy behavior).
   */
  get visualSlots(): Array<
    { kind: 'filter'; field: FieldOption } | { kind: 'group'; id: string; fields: FieldOption[] }
  > {
    const byKey = this.fieldByKey;
    if (this.args.layout) {
      const out: Array<
        | { kind: 'filter'; field: FieldOption }
        | { kind: 'group'; id: string; fields: FieldOption[] }
      > = [];
      for (const slot of this.args.layout) {
        if (slot.type === 'filter') {
          const f = byKey[slot.fieldKey];
          if (f) out.push({ kind: 'filter', field: f });
        } else {
          const fields = slot.fieldKeys.map((k) => byKey[k]).filter((x): x is FieldOption => !!x);
          out.push({ kind: 'group', id: slot.id, fields });
        }
      }
      return out;
    }
    return this.args.filters.map((field) => ({ kind: 'filter' as const, field }));
  }

  /** Groups available as move-into targets for a given field (excludes its own group) */
  groupsForField = (field: FieldOption): Array<{ id: string; label: string }> => {
    if (!this.args.layout) return [];
    const currentGroupId = findGroupForField(this.args.layout, field.key);
    const groups: Array<{ id: string; label: string }> = [];
    let idx = 1;
    for (const slot of this.args.layout) {
      if (slot.type === 'group') {
        if (slot.id !== currentGroupId) {
          groups.push({
            id: slot.id,
            label: this.intl.t('common.reporting.filters_zone.group_number', {
              number: String(idx),
            }),
          });
        }
        idx++;
      }
    }
    return groups;
  };

  isFieldInGroup = (field: FieldOption): boolean => {
    if (!this.args.layout) return false;
    return findGroupForField(this.args.layout, field.key) !== null;
  };

  // Type-narrowing accessors for the template. Glint can't narrow union types
  // inside {{#if}} blocks, so we return the narrowed slot or undefined.
  asFilterSlot = (
    slot:
      { kind: 'filter'; field: FieldOption } | { kind: 'group'; id: string; fields: FieldOption[] }
  ): { field: FieldOption } | undefined =>
    slot.kind === 'filter' ? { field: slot.field } : undefined;

  asGroupSlot = (
    slot:
      { kind: 'filter'; field: FieldOption } | { kind: 'group'; id: string; fields: FieldOption[] }
  ): { id: string; fields: FieldOption[]; number: number } | undefined => {
    if (slot.kind !== 'group') return undefined;
    return { id: slot.id, fields: slot.fields, number: this.groupNumberOf(slot.id) };
  };

  /** 1-based position of a group in the layout (only counting group slots) */
  groupNumberOf = (groupId: string): number => {
    if (!this.args.layout) return 0;
    let idx = 0;
    for (const slot of this.args.layout) {
      if (slot.type === 'group') {
        idx++;
        if (slot.id === groupId) return idx;
      }
    }
    return 0;
  };

  /**
   * Build the "move" dropdown items for a filter.
   * Short labels keep options readable in the compact dropdown.
   */
  moveTargetsFor = (field: FieldOption): Array<{ key: string; label: string }> => {
    const items: Array<{ key: string; label: string }> = [];
    if (this.isFieldInGroup(field)) {
      items.push({
        key: '__out__',
        label: this.intl.t('common.reporting.filters_zone.out_of_group'),
      });
    }
    for (const g of this.groupsForField(field)) {
      items.push({ key: g.id, label: g.label });
    }
    return items;
  };

  handleMoveTargetChange = (fieldKey: string, target: string | null) => {
    if (!target) return;
    if (target === '__out__') {
      this.args.onMoveOutOfGroup?.(fieldKey);
    } else {
      this.args.onMoveToGroup?.(fieldKey, target);
    }
  };

  handleAddGroupClick = () => {
    this.args.onAddGroup?.();
  };

  handleDissolveGroupClick = (groupId: string) => {
    this.args.onDissolveGroup?.(groupId);
  };

  isGroupEmpty = (slot: { kind: string; fields?: FieldOption[] }): boolean => {
    return slot.kind === 'group' && (slot.fields?.length ?? 0) === 0;
  };

  <template>
    <div class="tio-surface-panel min-h-[80px]" ...attributes>
      <div class="tio-row-tight mb-3">
        <span class="tio-text-sm-semibold tio-fg-secondary">
          {{if @title @title (t "common.reporting.filters_zone.title")}}
        </span>
        <span class="tio-text-xs-medium tio-bg-sunken tio-fg-muted px-1.5 py-0.5 rounded">
          {{this.filterCount}}
        </span>
      </div>
      {{#if @subtitle}}
        <div class="text-xs tio-fg-muted -mt-2 mb-3">{{@subtitle}}</div>
      {{/if}}

      {{#if this.hasFilters}}
        <div class="space-y-3">
          {{#each this.visualSlots as |slot|}}
            {{#let (this.asGroupSlot slot) (this.asFilterSlot slot) as |gs fs|}}
              {{#if gs}}
                <div
                  class="border border-dashed tio-border-brand-soft rounded-md p-3 tio-bg-brand-wash space-y-2"
                >
                  <div class="tio-row-between">
                    <span
                      class="tio-inline-row gap-1.5 tio-bg-brand-soft border tio-border-brand-soft rounded-md px-2 py-0.5 tio-text-xs-semibold tio-fg-brand"
                    >
                      {{t "common.reporting.filters_zone.or_group" number=gs.number}}
                    </span>
                    <button
                      type="button"
                      class="p-0.5 tio-fg-muted hover:tio-fg-secondary"
                      aria-label={{t
                        "common.reporting.filters_zone.dissolve_group_label"
                        number=gs.number
                      }}
                      title={{t "common.reporting.filters_zone.dissolve_group_title"}}
                      {{on "click" (fn this.handleDissolveGroupClick gs.id)}}
                    >
                      <PhX @size={{14}} />
                    </button>
                  </div>
                  {{#each gs.fields as |field|}}
                    {{#let
                      (getTypeConfig field.type) (this.detectOperator field)
                      as |typeConfig currentOp|
                    }}
                      <FilterRow
                        @field={{field}}
                        @currentOp={{currentOp}}
                        @typeConfig={{typeConfig}}
                        @parent={{this}}
                        @onRemove={{@onRemove}}
                        @moveTargets={{this.moveTargetsFor field}}
                        @onMoveTargetChange={{this.handleMoveTargetChange}}
                      />
                    {{/let}}
                  {{/each}}
                  {{#unless gs.fields.length}}
                    <p class="text-xs tio-fg-muted italic px-1">
                      {{t
                        "common.reporting.filters_zone.empty_group"
                        group=(t "common.reporting.filter_row.group_placeholder")
                      }}
                    </p>
                  {{/unless}}
                </div>
              {{else if fs}}
                {{#let
                  (getTypeConfig fs.field.type) (this.detectOperator fs.field)
                  as |typeConfig currentOp|
                }}
                  <FilterRow
                    @field={{fs.field}}
                    @currentOp={{currentOp}}
                    @typeConfig={{typeConfig}}
                    @parent={{this}}
                    @onRemove={{@onRemove}}
                    @moveTargets={{this.moveTargetsFor fs.field}}
                    @onMoveTargetChange={{this.handleMoveTargetChange}}
                  />
                {{/let}}
              {{/if}}
            {{/let}}
          {{/each}}

        </div>
      {{else}}
        <div
          class="tio-row-center h-16 text-sm tio-fg-muted border border-dashed tio-border-default rounded-md"
        >
          {{t "common.reporting.filters_zone.empty"}}
        </div>
      {{/if}}

      {{#if this.isLayoutMode}}
        <div class="mt-3">
          <Button
            @intent="default"
            @appearance="outlined"
            @size="sm"
            {{on "click" this.handleAddGroupClick}}
          >
            <PhPlus @size={{14}} />
            {{t "common.reporting.filters_zone.add_or_group"}}
          </Button>
        </div>
      {{/if}}
    </div>
  </template>
}

export default ActiveFiltersZone;
